/**
 * DataConnectorAlertModal
 *
 * One-shot notice when a custom researcher (data connector) broke: rejected
 * key, changed API, platform down. The backend writes one alert per connector
 * (World/{uid}/alerts, type data_connector_failed, deduped while open), so the
 * user sees it the next time they open the World DB — after a bulk run or an
 * agent hit the error in the background.
 *
 * "Got it" and "Open settings" both acknowledge it for good; the connector's
 * card in Settings → Integrations → Data Sources keeps showing the error until
 * it runs successfully again.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue, update } from 'firebase/database';
import { DATA_CONNECTOR_ALERT_TYPE, reasonLabel } from './dataConnectors';
import './OutreachJobPausedModal.css';

const DataConnectorAlertModal = ({ userId, onOpenSettings }) => {
  const [alerts, setAlerts] = useState([]);
  // A paused-agent modal may be open for the same failure; wait for it.
  const [jobPauseOpen, setJobPauseOpen] = useState(false);

  useEffect(() => {
    if (!userId) {
      setAlerts([]);
      setJobPauseOpen(false);
      return undefined;
    }
    return onValue(ref(database, `World/${userId}/alerts`), (snap) => {
      const now = Date.now();
      const open = Object.entries(snap.val() || {})
        .map(([id, a]) => ({ id, ...a }))
        .filter((a) => !a.dismissed && (!a.expiresAt || a.expiresAt > now));
      setJobPauseOpen(open.some((a) => a.type === 'outreach_job_paused'));
      setAlerts(open
        .filter((a) => a.type === DATA_CONNECTOR_ALERT_TYPE)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    });
  }, [userId]);

  const current = jobPauseOpen ? null : alerts[0] || null;

  const acknowledge = useCallback(() => {
    if (!userId || !current) return;
    setAlerts((prev) => prev.filter((a) => a.id !== current.id));
    update(ref(database, `World/${userId}/alerts/${current.id}`), {
      dismissed: true,
      dismissedAt: Date.now(),
    }).catch(() => {});
  }, [userId, current]);

  const openSettings = useCallback(() => {
    acknowledge();
    if (onOpenSettings) onOpenSettings();
  }, [acknowledge, onOpenSettings]);

  if (!current) return null;

  const name = current.metadata?.connectorName || 'Data source';
  const reason = current.metadata?.reason;
  const message = String(current.message || '').replace(/^Data source "[^"]*":\s*/, '')
    || 'The data source stopped working.';

  return (
    <div
      className="ojp-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dca-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) acknowledge(); }}
    >
      <div className="ojp-modal-sheet">
        <div className="ojp-modal-header">
          <div className="ojp-modal-icon-wrap">
            <i className="fa fa-plug-circle-exclamation" aria-hidden="true" />
          </div>
          <div className="ojp-modal-header-copy">
            <div className="ojp-modal-eyebrow">Custom researcher failed</div>
            <h2 id="dca-modal-title" className="ojp-modal-title">{name}</h2>
            {reason && (
              <div className="ojp-modal-reason-chip">
                <i className="fa fa-circle-exclamation" aria-hidden="true" />
                <span>{reasonLabel(reason)}</span>
              </div>
            )}
          </div>
          <button className="ojp-modal-close" onClick={acknowledge} aria-label="Close" type="button">×</button>
        </div>

        <div className="ojp-modal-body">
          <p className="ojp-modal-message">{message}</p>
          <p className="ojp-modal-hint">
            Research runs and agents using this data source skip it or pause until it works again.
            Open the data source to check the key or run a test.
          </p>
        </div>

        <div className="ojp-modal-actions">
          <button type="button" className="ojp-modal-btn secondary" onClick={acknowledge}>
            Got it
          </button>
          <button type="button" className="ojp-modal-btn primary" onClick={openSettings}>
            <i className="fa fa-arrow-right" aria-hidden="true" />
            <span>Open settings</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataConnectorAlertModal;
